import type { CreateSavedViewPayload, UpdateSavedViewPayload } from '@domain/schemas/payload.schema';
import { areFiltersEqual } from '@features/query-builder';
import type { SavedView } from '@queries/fetchSavedViews';
import { SAVED_VIEW_COUNTS_QUERY_KEY, savedViewQueries } from '@queries/fetchSavedViews';
import { createSavedView, deleteSavedView, updateSavedView } from '@src/mutations';
import { type QueryClient, useMutation, useQueryClient } from '@tanstack/react-query';

import { useActivateSavedViewInUrl, useDeselectSavedViewInUrl, useSavedViewUrlId } from './useSavedViewUrlSelection';

const refetchViewCount = (queryClient: QueryClient, view: SavedView) => {
  const count = savedViewQueries.count({ id: view.id, filters: view.filters });
  return queryClient
    .cancelQueries({ queryKey: count.queryKey })
    .then(() => {
      queryClient.invalidateQueries({ queryKey: count.queryKey, refetchType: 'none' });
      return queryClient.fetchQuery({ ...count, staleTime: 0 });
    })
    .catch(() => undefined);
};

export function useCreateSavedView() {
  const queryClient = useQueryClient();
  // Render-fresh URL closures: react-query re-captures pending mutation options every
  // render, so mid-flight navigation is visible to onSuccess while the hook is mounted.
  const currentViewId = useSavedViewUrlId();
  const activateInUrl = useActivateSavedViewInUrl();
  return useMutation({
    mutationFn: (input: CreateSavedViewPayload) => createSavedView(input),
    // Hook-level so activation survives the modal unmounting mid-flight; cache is seeded
    // before activation so the URL never carries a view the list can't resolve.
    onSuccess: (view) => {
      const previousViews = queryClient.getQueryData(savedViewQueries.list().queryKey) ?? [];
      queryClient.setQueryData(savedViewQueries.list().queryKey, [...previousViews, view]);
      const selectionHeld = currentViewId !== null && previousViews.some((v) => v.id === currentViewId);
      if (!selectionHeld) activateInUrl(view.id);
      return queryClient.invalidateQueries({ queryKey: savedViewQueries.list().queryKey });
    },
  });
}

export const UPDATE_SAVED_VIEW_MUTATION_KEY = ['saved-views', 'update'] as const;

export function useUpdateSavedView() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: UPDATE_SAVED_VIEW_MUTATION_KEY,
    mutationFn: ({ id, ...payload }: { id: string } & UpdateSavedViewPayload) => updateSavedView(id, payload),
    // Patched from the response so fresh name/filters survive even a failed reconciling refetch.
    onSuccess: (view) => {
      const prev = queryClient.getQueryData(savedViewQueries.list().queryKey)?.find((v) => v.id === view.id);
      queryClient.setQueryData(savedViewQueries.list().queryKey, (old) =>
        old?.map((v) => (v.id === view.id ? view : v)),
      );
      if (!prev || !areFiltersEqual(prev.filters, view.filters)) refetchViewCount(queryClient, view);
      return queryClient.invalidateQueries({ queryKey: savedViewQueries.list().queryKey });
    },
  });
}

export function useDeleteSavedView() {
  const queryClient = useQueryClient();
  const deselectInUrl = useDeselectSavedViewInUrl();
  return useMutation({
    mutationFn: (id: string) => deleteSavedView(id),
    // Hook-level so URL deselection survives the confirm modal unmounting mid-flight.
    onSuccess: (_res, deletedId) => {
      queryClient.setQueryData(savedViewQueries.list().queryKey, (old) => old?.filter((v) => v.id !== deletedId));
      queryClient.removeQueries({ queryKey: [...SAVED_VIEW_COUNTS_QUERY_KEY, deletedId] });
      deselectInUrl(deletedId);
      return queryClient.invalidateQueries({ queryKey: savedViewQueries.list().queryKey });
    },
  });
}
